import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronRight, Download, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarReaMemoriaXlsx,
  getReaMemoria,
  getReaParticipantes,
  getReaSerie,
  getReaSnapshots,
  type ReaDesglosePc,
  type ReaDesglosePpe,
  type ReaSombraDia,
  type ReaPpeEsperada,
  type ReaPaso,
  type ReaPunto,
  type ReaSnapshotResumen,
} from "@/lib/api";

/**
 * Reportes → Memoria de cálculo REA.
 *
 * Se elige un snapshot del monitor de MEMSIM (public.monitoreo_rea) y la
 * página reconstruye paso a paso cómo se llega al % REA vs MGP del semáforo:
 * insumos (MGP, PC, PPE) → REA = PC + PPE → REA/MGP → regla del umbral →
 * semáforo de CENACE. Cada paso trae su racional y, al final, se verifica
 * que lo recalculado cuadre con lo que publicó CENACE. Cada paso se despliega
 * al dar clic y enseña su racional y operandos; la PC y la PPE traen además su
 * desglose desde los estados de cuenta. Al pie, la evolución de la REA en
 * 6 meses móviles. El cálculo lo hace el backend; aquí sólo se pinta.
 */

// Paleta categórica validada (misma que /reportes/cauchy; ver skill dataviz).
// Orden fijo por entidad: REA, PC, PPE.
const COLOR_REA = "#2a78d6";
const COLOR_PC = "#eb6834";
const COLOR_PPE = "#1baf7a";


const nf2 = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf4 = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

const money = (n: number | null | undefined) =>
  n == null ? "—" : (n < 0 ? "−" : "") + "$" + nf2.format(Math.abs(n));
const pct = (n: number | null | undefined, dec = 4) =>
  n == null ? "—" : (n < 0 ? "−" : "") + (dec === 4 ? nf4 : nf2).format(Math.abs(n)) + " %";
const signo = (n: number | null | undefined, fmt: (x: number) => string) =>
  n == null ? "—" : n === 0 ? "0" : (n > 0 ? "+" : "−") + fmt(Math.abs(n));

const fmtMomento = (iso: string) =>
  new Date(iso).toLocaleString("es-MX", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
const fmtHora = (iso: string) =>
  new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

const hoyISO = () => new Date().toISOString().slice(0, 10);
function haceMeses(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}
/** Abrevia para el eje: 4,336,389 → '4.3 M' */
function compacto(x: number): string {
  const a = Math.abs(x);
  const sg = x < 0 ? "−" : "";
  if (a >= 1e6) return `${sg}${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(".0", "")} M`;
  if (a >= 1e3) return `${sg}${(a / 1e3).toFixed(0)} k`;
  return `${sg}${Math.round(a)}`;
}
const fmtDiaCorto = (iso: string) =>
  new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
function haceDias(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const TONO_ESTADO: Record<string, string> = {
  NORMAL: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ALERTA: "bg-red-50 text-red-700 border-red-200",
};

export function MemoriaReaPage() {
  const qp = useQuery({ queryKey: ["memoria-rea", "participantes"], queryFn: getReaParticipantes });
  const participantes = qp.data?.participantes ?? [];

  const [llave, setLlave] = useState<string | null>(null); // "SIN|C063"
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(hoyISO());
  const [fechaSel, setFechaSel] = useState<string | null>(null);
  const [momento, setMomento] = useState<string | null>(null);

  useEffect(() => {
    if (llave === null && participantes.length) {
      const p = participantes[0];
      setLlave(`${p.sistema}|${p.id_participante}`);
    }
  }, [participantes, llave]);

  const [sistema, participante] = (llave ?? "|").split("|");

  const qs = useQuery({
    queryKey: ["memoria-rea", "snapshots", sistema, participante, desde, hasta],
    queryFn: () => getReaSnapshots(sistema, participante, desde || undefined, hasta || undefined),
    enabled: Boolean(participante),
  });
  const snapshots = useMemo(() => qs.data?.snapshots ?? [], [qs.data]);

  // Días con snapshot (más reciente primero) y los momentos del día elegido.
  const porDia = useMemo(() => {
    const m = new Map<string, ReaSnapshotResumen[]>();
    for (const s of snapshots) {
      const dia = s.momento.slice(0, 10);
      m.set(dia, [...(m.get(dia) ?? []), s]);
    }
    return m;
  }, [snapshots]);
  const dias = useMemo(() => [...porDia.keys()], [porDia]);

  // Al cambiar la lista: por defecto el día y el momento más recientes.
  useEffect(() => {
    if (!snapshots.length) { setFechaSel(null); setMomento(null); return; }
    if (!fechaSel || !porDia.has(fechaSel)) {
      setFechaSel(dias[0]);
      setMomento(porDia.get(dias[0])![0].momento);
      return;
    }
    const delDia = porDia.get(fechaSel)!;
    if (!momento || !delDia.some((s) => s.momento === momento)) setMomento(delDia[0].momento);
  }, [snapshots, dias, porDia, fechaSel, momento]);

  const qm = useQuery({
    queryKey: ["memoria-rea", "memoria", sistema, participante, momento],
    queryFn: () => getReaMemoria(momento!, sistema, participante),
    enabled: Boolean(momento && participante),
  });
  const d = qm.data;

  const excel = useMutation({
    mutationFn: () => descargarReaMemoriaXlsx(momento!, sistema, participante),
  });

  // Evolución a 6 meses móviles (independiente del rango del selector).
  const seisMeses = useMemo(() => haceMeses(6), []);
  const qg = useQuery({
    queryKey: ["memoria-rea", "serie", sistema, participante, seisMeses],
    queryFn: () => getReaSerie(sistema, participante, seisMeses),
    enabled: Boolean(participante),
    staleTime: 5 * 60_000,
  });
  const [modoGrafica, setModoGrafica] = useState<"mxn" | "pct">("mxn");
  const [abiertos, setAbiertos] = useState<Set<number>>(() => new Set());
  const alternar = (n: number) =>
    setAbiertos((prev) => {
      const sig = new Set(prev);
      if (sig.has(n)) sig.delete(n); else sig.add(n);
      return sig;
    });

  const momentosDelDia = fechaSel ? porDia.get(fechaSel) ?? [] : [];
  const error = (qp.error ?? qs.error ?? qm.error ?? excel.error) as Error | null;

  return (
    <div className="space-y-5 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-slate-400" />
          Memoria de cálculo REA
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-4xl">
          Cómo se llega al porcentaje <b>REA vs MGP</b> del semáforo del encabezado. Se elige un
          snapshot del monitor de MEMSIM y se reconstruye el cálculo paso a paso: insumos de CENACE
          (MGP, PC, PPE), <b>REA = PC + PPE</b>, <b>REA ÷ MGP</b>, regla del umbral y semáforo.
          Al final se verifica que lo recalculado cuadre con lo publicado. Pesos mexicanos.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error.message}
        </div>
      )}

      {/* Selector del snapshot */}
      <section className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-x-5 gap-y-3">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Participante
          <select
            value={llave ?? ""}
            onChange={(e) => { setLlave(e.target.value); setFechaSel(null); setMomento(null); }}
            className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-800"
          >
            {participantes.map((p) => (
              <option key={`${p.sistema}|${p.id_participante}`} value={`${p.sistema}|${p.id_participante}`}>
                {p.nombre_pm ?? p.id_participante} · {p.id_participante} · {p.sistema}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                 className="rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                 className="rounded border border-slate-300 px-2 py-1 text-sm" />
        </label>

        <label className="flex flex-col gap-1 text-xs text-slate-500">
          Día del snapshot
          <select
            value={fechaSel ?? ""}
            onChange={(e) => { setFechaSel(e.target.value); setMomento(null); }}
            disabled={!dias.length}
            className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-800 disabled:opacity-50"
          >
            {dias.map((dia) => (
              <option key={dia} value={dia}>
                {new Date(dia + "T12:00:00").toLocaleDateString("es-MX", {
                  weekday: "short", day: "2-digit", month: "short", year: "numeric",
                })}{" "}
                ({porDia.get(dia)!.length})
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-col gap-1 text-xs text-slate-500">
          Hora
          <div className="flex flex-wrap gap-1">
            {momentosDelDia.map((s) => {
              const on = s.momento === momento;
              return (
                <button
                  key={s.momento}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setMomento(s.momento)}
                  title={`${s.semaforo ?? ""} · ${pct(s.pct, 2)}`}
                  className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-sm tabular-nums ${
                    on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <span className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: s.bullet_color ?? "#94a3b8" }} aria-hidden="true" />
                  {fmtHora(s.momento)}
                </button>
              );
            })}
            {!momentosDelDia.length && qs.isSuccess && (
              <span className="text-sm text-slate-400">Sin snapshots en el rango.</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => excel.mutate()}
          disabled={!d || excel.isPending}
          className="ml-auto inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {excel.isPending ? "Generando…" : "Excel"}
        </button>
      </section>

      {qm.isLoading && <div className="text-sm text-slate-500">Reconstruyendo el cálculo…</div>}

      {d && (
        <>
          {/* Resumen del snapshot */}
          <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Tarjeta titulo="Snapshot" valor={fmtMomento(d.snapshot.momento)} chico
                     nota={`${d.snapshot.nombre_pm ?? ""} · ${d.snapshot.id_participante} · ${d.snapshot.sistema}`} />
            <Tarjeta titulo="MGP (garantía)" valor={money(d.snapshot.mgp)} />
            <Tarjeta titulo="REA = PC + PPE" valor={money(d.snapshot.rea)}
                     color={d.snapshot.rea != null && d.snapshot.rea < 0 ? "text-emerald-700" : "text-slate-900"}
                     nota={d.derivados.posicion ? `posición ${d.derivados.posicion}` : undefined} />
            <Tarjeta titulo="REA vs MGP" valor={pct(d.snapshot.pct, 2)}
                     color={d.snapshot.pct != null && Math.abs(d.snapshot.pct) >= d.derivados.umbral_pct ? "text-red-700" : "text-slate-900"}
                     nota={`umbral ${d.derivados.umbral_pct.toFixed(0)} % en valor absoluto`} />
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-xs text-slate-500">Semáforo CENACE</div>
              <div className="mt-1 flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: d.snapshot.bullet_color ?? "#94a3b8",
                               boxShadow: `0 0 0 3px ${d.snapshot.bullet_color ?? "#94a3b8"}22` }} />
                <span className={`text-sm font-semibold rounded-full border px-2 py-0.5 ${
                  TONO_ESTADO[d.snapshot.semaforo ?? ""] ?? "bg-slate-50 text-slate-600 border-slate-200"
                }`}>
                  {d.snapshot.semaforo ?? "—"}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">bullet {d.snapshot.bullet_color ?? "—"}</div>
            </div>
          </section>

          {/* Pasos */}
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                Memoria de cálculo
                <span className="ml-2 font-normal text-xs text-slate-400">clic en un concepto para ver el detalle</span>
              </span>
              <button
                type="button"
                onClick={() => setAbiertos(abiertos.size === d.pasos.length ? new Set() : new Set(d.pasos.map((p) => p.n)))}
                className="text-xs text-slate-500 hover:text-slate-800"
              >
                {abiertos.size === d.pasos.length ? "Plegar todo" : "Desplegar todo"}
              </button>
            </div>
            <ol className="divide-y divide-slate-100">
              {d.pasos.map((p) => (
                <Paso key={p.n} p={p} abierto={abiertos.has(p.n)} onToggle={() => alternar(p.n)}
                      desglose={p.campo === "pc" ? d.desglose_pc : undefined}
                      desglosePpe={p.campo === "ppe" ? d.desglose_ppe : undefined} />
              ))}
            </ol>
          </section>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Verificaciones */}
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-sm font-medium text-slate-700">
                Verificaciones contra lo publicado por CENACE
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Comprobación</th>
                    <th className="text-right px-3 py-2 font-medium">Recalculado</th>
                    <th className="text-right px-3 py-2 font-medium">Publicado</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.verificaciones.map((v) => (
                    <tr key={v.clave}>
                      <td className="px-4 py-2 text-slate-700">{v.etiqueta}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtValor(v.calculado, v.clave)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtValor(v.publicado, v.clave)}</td>
                      <td className="px-3 py-2 text-center">
                        {v.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                            <Check className="h-3.5 w-3.5" /> cuadra
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium"
                                title={v.clave === "semaforo"
                                  ? "El bullet de CENACE manda; la regla del umbral es sólo el respaldo del ETL."
                                  : undefined}>
                            <X className="h-3.5 w-3.5" /> difiere
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                <div className="flex justify-between gap-3">
                  <span>Holgura hasta ALERTA ({d.derivados.umbral_pct.toFixed(0)} % de MGP − |REA|)</span>
                  <b className="tabular-nums text-slate-700">{money(d.derivados.holgura_hasta_alerta)}</b>
                </div>
                <div className="flex justify-between gap-3">
                  <span>Garantía no comprometida (MGP − máx(REA, 0))</span>
                  <b className="tabular-nums text-slate-700">{money(d.derivados.garantia_libre)}</b>
                </div>
              </div>
            </section>

            {/* Comparación con el anterior */}
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-sm font-medium text-slate-700">
                Cambio contra el snapshot anterior
                {d.anterior && (
                  <span className="font-normal text-slate-500"> · {fmtMomento(d.anterior.momento)}</span>
                )}
              </div>
              {d.anterior ? (
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Concepto</th>
                      <th className="text-right px-3 py-2 font-medium">Anterior</th>
                      <th className="text-right px-3 py-2 font-medium">Actual</th>
                      <th className="text-right px-3 py-2 font-medium">Δ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(["mgp", "pc", "ppe", "rea"] as const).map((k) => (
                      <tr key={k} className={k === "rea" ? "font-medium bg-slate-50" : ""}>
                        <td className="px-4 py-2 uppercase text-slate-700">{k}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(d.anterior![k])}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(d.snapshot[k])}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${tonoDelta(d.anterior!.delta[k])}`}>
                          {signo(d.anterior!.delta[k], (x) => "$" + nf2.format(x))}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-medium bg-slate-50">
                      <td className="px-4 py-2 text-slate-700">REA vs MGP</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(d.anterior.pct, 2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(d.snapshot.pct, 2)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${tonoDelta(d.anterior.delta.pct)}`}>
                        {signo(d.anterior.delta.pct, (x) => nf2.format(x) + " pp")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-3 text-sm text-slate-500">Es el primer snapshot registrado.</div>
              )}
              <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
                Un Δ positivo acerca la posición al lado deudor (consume garantía); negativo la aleja.
              </div>
            </section>
          </div>

          {/* Evolución 6 meses */}
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-700">
                Evolución de la REA · 6 meses móviles
              </span>
              <span className="text-xs text-slate-500">
                desde {fmtDiaCorto(seisMeses + "T12:00:00")} · {qg.data?.serie.length ?? 0} snapshots
              </span>
              <div className="ml-auto inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                {([["mxn", "Pesos (REA, PC, PPE)"], ["pct", "% REA vs MGP"]] as const).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={modoGrafica === v}
                    onClick={() => setModoGrafica(v)}
                    className={`px-3 py-1 text-xs rounded ${
                      modoGrafica === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3">
              {qg.isLoading && <div className="text-sm text-slate-500">Cargando la serie…</div>}
              {qg.data && qg.data.serie.length > 0 && (
                <GraficaRea serie={qg.data.serie} modo={modoGrafica} umbral={qg.data.umbral_pct}
                            seleccionado={d.snapshot.momento} />
              )}
              {qg.isSuccess && qg.data.serie.length === 0 && (
                <div className="text-sm text-slate-500">Sin snapshots en los últimos 6 meses.</div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-400">
              Línea vertical punteada = snapshot seleccionado arriba. En pesos, los valores negativos
              son posición acreedora (CENACE debe a MPP). En %, las líneas rojas en
              ±{qg.data?.umbral_pct ?? 80} % marcan el umbral de ALERTA.
            </div>
          </section>

          <p className="text-xs text-slate-400">
            Fuente: {d.fuente.origen} · tabla {d.fuente.tabla} · {d.fuente.etl}
            {d.snapshot.creado_en && ` · capturado ${fmtMomento(d.snapshot.creado_en)}`}
          </p>
        </>
      )}
    </div>
  );
}

/** Colorea el Δ: más exposición (hacia deudor) en rojo, menos en verde. */
function tonoDelta(x: number | null): string {
  if (x == null || x === 0) return "text-slate-500";
  return x > 0 ? "text-red-700" : "text-emerald-700";
}

function fmtValor(v: number | string | null, clave: string): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  return clave === "pct" ? pct(v) : money(v);
}

function Paso({ p, abierto, onToggle, desglose, desglosePpe }: {
  p: ReaPaso; abierto: boolean; onToggle: () => void;
  desglose?: ReaDesglosePc; desglosePpe?: ReaDesglosePpe;
}) {
  const destacado = p.n === 4 || p.n === 5 || p.n === 7;
  const valor =
    p.valor == null ? "—"
    : typeof p.valor === "string" ? p.valor
    : p.unidad === "%" ? pct(p.valor)
    : money(p.valor);
  const tonoValor =
    typeof p.valor === "string" ? (TONO_ESTADO[p.valor] ?? "") : "";
  const Icono = abierto ? ChevronDown : ChevronRight;
  return (
    <li className={destacado ? "bg-slate-50/60" : ""}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={abierto}
        className="w-full text-left px-4 py-3 grid gap-x-4 items-center grid-cols-[1.25rem_2rem_1fr_auto] hover:bg-slate-100/60"
      >
        <Icono className="h-4 w-4 text-slate-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-400 tabular-nums">{p.n}</span>
        <span className="min-w-0">
          <span className={`block text-sm ${destacado ? "font-semibold" : "font-medium"} text-slate-900`}>{p.titulo}</span>
          <code className="block mt-0.5 text-xs text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 w-fit">
            {p.formula}
          </code>
        </span>
        <span className="text-right">
          {tonoValor ? (
            <span className={`inline-block text-sm font-semibold rounded-full border px-2.5 py-0.5 ${tonoValor}`}>{valor}</span>
          ) : (
            <span className={`text-base tabular-nums ${destacado ? "font-semibold" : "font-medium"} text-slate-900`}>{valor}</span>
          )}
          {p.unidad === "MXN" && <span className="block text-[11px] text-slate-400">{p.unidad}</span>}
        </span>
      </button>

      {abierto && (
        <div className="px-4 pb-4 pl-[5.25rem] grid gap-4">
          <div>
            {p.operandos && (
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 tabular-nums">
                {Object.entries(p.operandos).map(([k, v]) => (
                  <span key={k}>
                    {k} = <b className="text-slate-700">{
                      v == null ? "—"
                      : typeof v === "string" ? v
                      : k === "umbral" || k.includes("REAvsMGP") ? pct(v) : money(v)
                    }</b>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.racional}</p>
          </div>
          {desglose && <DesglosePc d={desglose} />}
          {desglosePpe && <DesglosePpe d={desglosePpe} />}
        </div>
      )}
    </li>
  );
}

/** Paso 3 · días operados que al corte no tenían estado de cuenta, con lo que
 *  cada uno liquidó realmente (si ya salió su edocta) frente a la PPE estimada.
 *  Informativo: la fórmula de CENACE no se reproduce. */
function DesglosePpe({ d }: { d: ReaDesglosePpe }) {
  const ref = d.referencia_7d;
  return (
    <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden text-sm">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium text-slate-700">Días operados sin estado de cuenta al corte</span>
        <span className="text-xs text-slate-500">
          corte {fmtDia(d.fecha_corte)} · {d.n_dias} días pendientes
          {d.ultimo_oper_emitido && ` · último edocta emitido: día operado ${fmtDia(d.ultimo_oper_emitido)}`}
        </span>
        <span className="ml-auto text-xs text-slate-500">
          {d.completo
            ? `${d.n_con_edocta} de ${d.n_dias} ya con edocta`
            : `${d.n_con_edocta} de ${d.n_dias} ya con edocta · faltan ${d.n_dias - d.n_con_edocta}`}
        </span>
      </div>

      {d.sin_cobertura ? (
        <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50">
          No hay estados de cuenta cargados antes de esa fecha.
        </div>
      ) : (
        <table className="w-full tabular-nums">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="text-left px-3 py-1.5 font-medium">Día operado</th>
              <th className="text-left px-2 py-1.5 font-medium">Edocta emitido</th>
              <th className="text-right px-2 py-1.5 font-medium text-violet-700">Cargos sombra</th>
              <th className="text-right px-2 py-1.5 font-medium text-violet-700">Créditos sombra</th>
              <th className="text-right px-2 py-1.5 font-medium text-violet-700">Neto sombra</th>
              <th className="text-right px-3 py-1.5 font-medium">Neto real</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.dias.map((x) => (
              <tr key={x.fecha_oper} className={x.con_edocta ? "text-slate-700" : "text-slate-500"}>
                <td className="px-3 py-1 whitespace-nowrap">{fmtDia(x.fecha_oper)}</td>
                <td className={`px-2 py-1 whitespace-nowrap ${x.fecha_emis ? "" : "italic text-slate-400"}`}>
                  {x.fecha_emis ? fmtDia(x.fecha_emis) : "aún sin estado de cuenta"}
                </td>
                <td className="px-2 py-1 text-right text-violet-800">{x.sombra ? money(x.sombra.cargos) : "—"}</td>
                <td className="px-2 py-1 text-right text-violet-800">{x.sombra ? money(x.sombra.creditos) : "—"}</td>
                <td className={`px-2 py-1 text-right ${x.sombra == null ? "" : x.sombra.neto < 0 ? "text-emerald-700" : "text-red-700"}`}
                    title={x.sombra ? tituloSombra(x.sombra) : undefined}>
                  {x.sombra ? money(x.sombra.neto) : "—"}
                  {x.sombra?.pml_faltante && <span className="ml-1 text-amber-600" title="Faltan horas de PML MDA">*</span>}
                </td>
                <td className={`px-3 py-1 text-right ${x.neto == null ? "text-slate-400" : x.neto < 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {x.neto == null ? "—" : money(x.neto)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="text-sm border-t border-slate-200 bg-slate-50/60">
            <tr className="font-semibold text-slate-900">
              <td colSpan={2} className="px-3 py-1.5">
                Total de los días pendientes{!d.completo && d.n_con_edocta > 0 && " (real parcial)"}
              </td>
              <td className="px-2 py-1.5 text-right text-violet-800">{d.sombra ? money(d.sombra.cargos) : "—"}</td>
              <td className="px-2 py-1.5 text-right text-violet-800">{d.sombra ? money(d.sombra.creditos) : "—"}</td>
              <td className="px-2 py-1.5 text-right">{d.sombra ? money(d.sombra.neto) : "—"}</td>
              <td className="px-3 py-1.5 text-right">{money(d.neto_real)}</td>
            </tr>
            {d.sombra && (
              <tr className="font-semibold text-violet-900">
                <td colSpan={5} className="px-3 py-1">
                  PPE sombra (neto real donde ya hay edocta, sombra donde no)
                </td>
                <td className="px-3 py-1 text-right">{money(d.sombra.neto_estimado)}</td>
              </tr>
            )}
            <tr className="text-slate-600">
              <td colSpan={5} className="px-3 py-1">
                PPE publicada por CENACE · {money(d.ppe_por_dia)} por día pendiente
              </td>
              <td className="px-3 py-1 text-right">{money(d.ppe_publicada)}</td>
            </tr>
            {d.sombra?.diferencia_ppe != null && (
              <tr className="text-slate-600">
                <td colSpan={5} className="px-3 py-1">PPE publicada − PPE sombra</td>
                <td className={`px-3 py-1 text-right ${tonoDelta(d.sombra.diferencia_ppe)}`}>
                  {signo(d.sombra.diferencia_ppe, (x) => "$" + nf2.format(x))}
                </td>
              </tr>
            )}
            {d.diferencia != null && (
              <tr className="text-slate-600">
                <td colSpan={5} className="px-3 py-1">Estimación de CENACE − neto real</td>
                <td className={`px-3 py-1 text-right ${tonoDelta(d.diferencia)}`}>
                  {signo(d.diferencia, (x) => "$" + nf2.format(x))}
                </td>
              </tr>
            )}
            {ref && (
              <tr className="text-slate-500 text-xs">
                <td colSpan={5} className="px-3 py-1">
                  Referencia del manual (promedio de los {ref.dias} días operados previos, {fmtDia(ref.desde)} a {fmtDia(ref.hasta)}):
                  {" "}neto {money(ref.neto_dia)} por día → proyectado a {d.n_dias} días
                </td>
                <td className="px-3 py-1 text-right">{money(ref.neto_proyectado)}</td>
              </tr>
            )}
          </tfoot>
        </table>
      )}
      {d.esperada && <PpeEsperada e={d.esperada} />}
      <div className="px-3 py-1.5 border-t border-slate-100 text-[11px] text-slate-400">
        Sombra: TBFin del ETRM enviadas a CENACE (más las automáticas, con el modelo del contrato), MWh de cada
        hora × PML MDA del nodo del contrato + 16 % de IVA; MPP vendedor = cargo, comprador = crédito. Es el mismo
        cálculo de /sombra-cenace y cuadra al centavo con el edocta cuando éste sale. Pasa el cursor sobre el neto
        sombra para ver los contratos. Informativo: la PPE la estima CENACE con <code>{d.formula_manual}</code> y
        {" "}<code>{d.formula_cpmcp}</code> (Manual de Garantías de Cumplimiento, DOF 16-mar-2016); sus insumos no
        están en el ETRM, así que no se verifica. Fuente: {d.fuente}; etrm.tbfin_calculo y public.pml_mda.
      </div>
    </div>
  );
}

function tituloSombra(s: ReaSombraDia): string {
  return s.contratos
    .map((c) => `${c.lado === "cargo" ? "Cargo" : "Crédito"} · ${c.contrato} (${c.origen}) · ${c.nodo} · `
      + `${nf2.format(c.mwh)} MWh · $${nf2.format(c.con_iva)} con IVA`)
    .join("\n");
}

/** PPE esperada: media de las N semanas previas en el mismo día de la semana y
 *  hora de corrida, con la etiqueta típica/atípica y el perfil semanal completo
 *  (la celda del snapshot resaltada). Es estadístico, no la fórmula de CENACE. */
function PpeEsperada({ e }: { e: ReaPpeEsperada }) {
  const [verPerfil, setVerPerfil] = useState(false);
  const tono =
    e.etiqueta === "típica" ? "bg-emerald-100 text-emerald-800"
    : e.etiqueta === "atípica" ? "bg-amber-100 text-amber-800"
    : e.etiqueta === "muy atípica" ? "bg-red-100 text-red-800"
    : "bg-slate-100 text-slate-600";
  const horas = e.perfil[0]?.celdas.map((c) => c.hora) ?? [];
  return (
    <div className="border-t border-slate-200 bg-blue-50/40">
      <div className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium text-slate-700">PPE esperada</span>
        <span className="text-xs text-slate-500">
          {e.dia} {String(e.hora).padStart(2, "0")} h · media de {e.n} snapshots en {e.semanas} semanas
          ({fmtDia(e.desde)} a {fmtDia(e.hasta)})
        </span>
        <span className="ml-auto tabular-nums font-semibold text-slate-900">{money(e.esperada)}</span>
        {e.sd != null && <span className="text-xs text-slate-500 tabular-nums">± {money(e.sd)}</span>}
        {e.etiqueta && (
          <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${tono}`} title={`z = ${e.z}`}>
            {e.etiqueta}
          </span>
        )}
      </div>
      {e.diferencia != null && (
        <div className="px-3 pb-2 text-xs text-slate-600 tabular-nums">
          Publicada {money(e.publicada)} − esperada {money(e.esperada)} ={" "}
          <span className={tonoDelta(e.diferencia)}>{signo(e.diferencia, (x) => "$" + nf2.format(x))}</span>
          {e.z != null && <> ({e.z > 0 ? "+" : ""}{e.z} desviaciones)</>}
          {" · "}
          <button type="button" onClick={() => setVerPerfil((v) => !v)} className="text-blue-600 hover:underline">
            {verPerfil ? "ocultar perfil semanal" : "ver perfil semanal"}
          </button>
        </div>
      )}
      {verPerfil && (
        <div className="px-3 pb-2 overflow-x-auto">
          <table className="text-xs tabular-nums">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left pr-3 py-1 font-medium">Media (miles MXN)</th>
                {horas.map((h) => (
                  <th key={h} className="text-right px-2 py-1 font-medium">{String(h).padStart(2, "0")} h</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {e.perfil.map((f) => (
                <tr key={f.dow} className="border-t border-slate-100">
                  <td className="pr-3 py-0.5 capitalize text-slate-600">{f.dia}</td>
                  {f.celdas.map((c) => {
                    const actual = f.dia === e.dia && c.hora === e.hora;
                    return (
                      <td
                        key={c.hora}
                        title={c.n ? `${c.n} snapshots · ± ${money(c.sd)}` : "sin datos"}
                        className={`text-right px-2 py-0.5 ${actual ? "bg-blue-100 font-semibold text-blue-900 rounded" : "text-slate-700"}`}
                      >
                        {c.media == null ? "—" : nf2.format(c.media / 1000).replace(/.d+$/, "")}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-slate-400 mt-1">{e.metodo}</div>
        </div>
      )}
    </div>
  );
}

const fmtDia = (iso: string) =>
  new Date(iso + "T12:00:00").toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });

/** Paso 2 · partidas de los estados de cuenta que suman la PC, agrupadas por
 *  fecha de pago, con el cruce contra el número publicado por CENACE. */
function DesglosePc({ d }: { d: ReaDesglosePc }) {
  const grupos = useMemo(() => {
    const m = new Map<string, typeof d.partidas>();
    for (const p of d.partidas) m.set(p.fecha_pago, [...(m.get(p.fecha_pago) ?? []), p]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [d.partidas]);
  const excluidas = d.partidas.filter((p) => !p.incluida).length;

  return (
    <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden text-sm">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium text-slate-700">Desglose de la PC por estado de cuenta</span>
        <span className="text-xs text-slate-500">
          corte {fmtDia(d.fecha_corte)} · {d.n_partidas} partidas
          {excluidas > 0 && ` · ${excluidas} fuera del corte`}
        </span>
        <span className="ml-auto">
          {d.cuadra ? (
            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
              <Check className="h-3.5 w-3.5" /> cuadra con lo publicado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
              <X className="h-3.5 w-3.5" /> difiere {signo(d.diferencia, (x) => "$" + nf2.format(x))}
            </span>
          )}
        </span>
      </div>

      {d.sin_cobertura && (
        <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
          No hay estados de cuenta cargados para esa fecha
          {d.cobertura.desde && ` (la carga empieza el ${fmtDia(d.cobertura.desde)})`}.
        </div>
      )}
      {d.variante !== "base" && (
        <div className="px-3 py-2 text-xs text-slate-600 bg-slate-50/60 border-b border-slate-100">
          Ajuste intradía: {d.variante_descripcion}
        </div>
      )}

      {grupos.length > 0 && (
        <table className="w-full tabular-nums">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="text-left px-3 py-1.5 font-medium">Edocta (día operado)</th>
              <th className="text-left px-2 py-1.5 font-medium">Emitido</th>
              <th className="text-left px-2 py-1.5 font-medium">Concepto</th>
              <th className="text-right px-2 py-1.5 font-medium">Monto c/IVA</th>
              <th className="text-right px-3 py-1.5 font-medium">Efecto en PC</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {grupos.map(([fechaPago, partidas]) => {
              const inc = partidas.filter((p) => p.incluida);
              const cargos = inc.filter((p) => p.lado === "cargo").reduce((a, p) => a + p.monto, 0);
              const creditos = inc.filter((p) => p.lado === "credito").reduce((a, p) => a + p.monto, 0);
              return (
                <FragmentoGrupo key={fechaPago} fechaPago={fechaPago} partidas={partidas}
                                cargos={cargos} creditos={creditos} />
              );
            })}
          </tbody>
          <tfoot className="text-sm border-t border-slate-200 bg-slate-50/60">
            <tr>
              <td colSpan={3} className="px-3 py-1 text-slate-600">Total cargos (CENACE cobra a MPP)</td>
              <td className="px-2 py-1 text-right">{money(d.total_cargos)}</td>
              <td className="px-3 py-1 text-right text-red-700">{signo(d.total_cargos, (x) => "$" + nf2.format(x))}</td>
            </tr>
            <tr>
              <td colSpan={3} className="px-3 py-1 text-slate-600">Total créditos (MPP cobra a CENACE)</td>
              <td className="px-2 py-1 text-right">{money(d.total_creditos)}</td>
              <td className="px-3 py-1 text-right text-emerald-700">{signo(-d.total_creditos, (x) => "$" + nf2.format(x))}</td>
            </tr>
            <tr className="font-semibold text-slate-900">
              <td colSpan={4} className="px-3 py-1.5">PC reconstruida = cargos − créditos</td>
              <td className="px-3 py-1.5 text-right">{money(d.total)}</td>
            </tr>
            <tr className="text-slate-600">
              <td colSpan={4} className="px-3 py-1">PC publicada por CENACE (S12)</td>
              <td className="px-3 py-1 text-right">{money(d.pc_publicado)}</td>
            </tr>
          </tfoot>
        </table>
      )}
      <div className="px-3 py-1.5 border-t border-slate-100 text-[11px] text-slate-400">
        {d.regla} Las partidas en gris no entran al corte de este snapshot. Fuente: {d.fuente}.
      </div>
    </div>
  );
}

function FragmentoGrupo({ fechaPago, partidas, cargos, creditos }: {
  fechaPago: string; partidas: ReaDesglosePc["partidas"]; cargos: number; creditos: number;
}) {
  return (
    <>
      <tr className="bg-slate-50 text-xs text-slate-600">
        <td colSpan={3} className="px-3 py-1 font-medium">
          Vence {fmtDia(fechaPago)}
        </td>
        <td className="px-2 py-1 text-right">
          <span className="text-red-700">{money(cargos)}</span>
          <span className="text-slate-400"> / </span>
          <span className="text-emerald-700">{money(creditos)}</span>
        </td>
        <td className="px-3 py-1 text-right font-medium text-slate-700">{money(cargos - creditos)}</td>
      </tr>
      {partidas.map((p) => (
        <tr key={`${p.fecha_oper}|${p.subcuenta}|${p.lado}|${p.fecha_pago}`}
            className={p.incluida ? "text-slate-700" : "text-slate-400 italic"}>
          <td className="px-3 py-1 whitespace-nowrap">{fmtDia(p.fecha_oper)}
            <span className="text-[11px] text-slate-400"> · {p.subcuenta}</span>
          </td>
          <td className="px-2 py-1 whitespace-nowrap">
            {fmtDia(p.fecha_emis)}
            {p.emitido_hoy && (
              <span className="ml-1 text-[10px] rounded bg-amber-50 text-amber-700 border border-amber-200 px-1">hoy</span>
            )}
          </td>
          <td className="px-2 py-1">
            {p.lado === "cargo" ? "Cargo · CENACE cobra a MPP" : "Crédito · MPP cobra a CENACE"}
            {p.vence_hoy && (
              <span className="ml-1 text-[10px] rounded bg-slate-100 text-slate-600 border border-slate-200 px-1">
                vence hoy{p.incluida ? ", sin aplicar" : ""}
              </span>
            )}
          </td>
          <td className="px-2 py-1 text-right">{money(p.monto)}</td>
          <td className={`px-3 py-1 text-right ${
            !p.incluida ? "" : p.lado === "cargo" ? "text-red-700" : "text-emerald-700"
          }`}>
            {p.incluida ? signo(p.signado, (x) => "$" + nf2.format(x)) : "—"}
          </td>
        </tr>
      ))}
    </>
  );
}

function GraficaRea({ serie, modo, umbral, seleccionado }: {
  serie: ReaPunto[]; modo: "mxn" | "pct"; umbral: number; seleccionado: string;
}) {
  const datos = useMemo(
    () => serie.map((p) => ({ ...p, t: new Date(p.momento).getTime() })),
    [serie],
  );
  const tSel = new Date(seleccionado).getTime();
  const fmtEje = (v: number) => (modo === "pct" ? `${v} %` : compacto(v));
  const fmtTip = (v: number) => (modo === "pct" ? pct(v, 2) : money(v));

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={datos} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]}
            tickFormatter={(t: number) => fmtDiaCorto(new Date(t).toISOString())}
            tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} minTickGap={40}
          />
          <YAxis
            tickFormatter={fmtEje} tick={{ fontSize: 11, fill: "#64748b" }}
            axisLine={false} tickLine={false} width={64}
          />
          <Tooltip
            labelFormatter={(t) => fmtMomento(new Date(Number(t)).toISOString())}
            formatter={(v, name) => [fmtTip(Number(v)), String(name)]}
            contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine x={tSel} stroke="#0f172a" strokeDasharray="4 3" />
          {modo === "mxn" ? (
            <>
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Line type="monotone" dataKey="rea" name="REA" stroke={COLOR_REA} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="pc" name="PC" stroke={COLOR_PC} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="ppe" name="PPE" stroke={COLOR_PPE} strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </>
          ) : (
            <>
              <ReferenceLine y={umbral} stroke="#dc2626" strokeDasharray="6 3"
                             label={{ value: `+${umbral} % ALERTA`, position: "insideTopRight", fontSize: 11, fill: "#dc2626" }} />
              <ReferenceLine y={-umbral} stroke="#dc2626" strokeDasharray="6 3"
                             label={{ value: `−${umbral} %`, position: "insideBottomRight", fontSize: 11, fill: "#dc2626" }} />
              <ReferenceLine y={0} stroke="#94a3b8" />
              <Line type="monotone" dataKey="pct" name="REA vs MGP" stroke={COLOR_REA} strokeWidth={2} dot={false} isAnimationActive={false} />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Tarjeta({ titulo, valor, nota, color = "text-slate-900", chico = false }: {
  titulo: string; valor: string; nota?: string; color?: string; chico?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-xs text-slate-500">{titulo}</div>
      <div className={`mt-1 ${chico ? "text-sm" : "text-lg"} font-semibold tabular-nums ${color}`}>{valor}</div>
      {nota && <div className="text-[11px] text-slate-400 mt-0.5 truncate" title={nota}>{nota}</div>}
    </div>
  );
}
